import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { CampaignStatus, CampaignType } from '../../../enums'

/**
 * Statut de livraison pour une cible de campagne
 */
export enum DeliveryStatus {
  PENDING = 'PENDING',
  QUEUED = 'QUEUED',
  SENDING = 'SENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

/**
 * Réponse pour une cible de campagne
 */
export class CampaignTargetResponse {
  @ApiProperty({ description: 'ID unique de la cible' })
  id!: string

  @ApiProperty({ description: 'Date de création' })
  createdAt!: Date

  @ApiProperty({ description: 'Date de mise à jour' })
  updatedAt!: Date

  @ApiProperty({ description: 'ID de la campagne parente' })
  campaignId!: string

  @ApiPropertyOptional({
    description: "ID de l'établissement ciblé",
    example: '770e8400-e29b-41d4-a716-446655440003',
  })
  establishmentId?: string

  @ApiPropertyOptional({
    description: "UAI de l'établissement",
    example: '0755678A',
  })
  establishmentUai?: string

  @ApiPropertyOptional({
    description: 'Email du contact destinataire',
    example: 'admin@lycee-exemple.fr',
  })
  contactEmail?: string

  @ApiPropertyOptional({
    description: "Nom du contact ou établissement",
    example: 'Lycée Victor Hugo',
  })
  contactName?: string

  @ApiPropertyOptional({
    description: 'Rôle du contact',
    example: 'ADMIN',
  })
  contactRole?: string

  @ApiPropertyOptional({
    description: "ID de l'utilisateur ciblé",
    example: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
  })
  userId?: string

  @ApiProperty({
    enum: DeliveryStatus,
    description: 'Statut de livraison de la communication à cette cible',
    example: DeliveryStatus.SENT,
  })
  deliveryStatus!: DeliveryStatus

  @ApiPropertyOptional({
    description: "Date d'envoi effectif",
    example: '2025-11-24T14:47:10.435Z',
  })
  sentAt?: Date

  @ApiPropertyOptional({
    description: "Date d'ouverture par le destinataire (tracking)",
    example: null,
  })
  openedAt?: Date

  @ApiPropertyOptional({
    description: "Message d'erreur en cas d'échec",
    example: null,
  })
  lastError?: string

  @ApiProperty({
    description: "Nombre de tentatives d'envoi",
    example: 0,
  })
  retryCount!: number
}

/**
 * Réponse pour une campagne
 */
export class CampaignResponse {
  @ApiProperty({
    description: 'ID unique de la campagne',
    example: '341641ae-bea7-468c-8647-9adb1d2ea510',
  })
  id!: string

  @ApiProperty({
    description: 'Date de création',
    example: '2025-11-24T14:46:39.113Z',
  })
  createdAt!: Date

  @ApiProperty({
    description: 'Date de dernière mise à jour',
    example: '2025-11-24T14:47:10.365Z',
  })
  updatedAt!: Date

  @ApiProperty({
    description: 'Nom de la campagne',
    example: 'Réunion Parents-Professeurs Mars 2025',
  })
  name!: string

  @ApiPropertyOptional({
    description: 'Description détaillée de la campagne',
    example:
      'Campagne de communication pour informer les parents de la réunion trimestrielle',
  })
  description?: string

  @ApiProperty({
    enum: CampaignType,
    description:
      'Type de campagne: ANNOUNCEMENT (annonces), EMAIL_CAMPAIGN (emails), MIXED (annonces + emails)',
    example: CampaignType.MIXED,
  })
  type!: CampaignType

  @ApiProperty({
    enum: CampaignStatus,
    description: 'Statut actuel de la campagne',
    example: CampaignStatus.RUNNING,
  })
  status!: CampaignStatus

  @ApiPropertyOptional({
    description: 'Date de lancement planifiée (null si lancement immédiat)',
    example: null,
  })
  scheduledAt?: Date

  @ApiPropertyOptional({
    description: 'Date de démarrage effectif de la campagne',
    example: '2025-11-24T14:47:10.362Z',
  })
  startedAt?: Date

  @ApiPropertyOptional({
    description: 'Date de fin de la campagne (null si en cours)',
    example: null,
  })
  completedAt?: Date

  @ApiProperty({
    description: "ID de l'utilisateur créateur",
    example: 'dev-user-id',
  })
  createdByUserId!: string

  @ApiProperty({
    description: "Nom de l'utilisateur créateur",
    example: 'Development User',
  })
  createdByName!: string

  @ApiProperty({
    description: "Rôle de l'utilisateur créateur",
    example: 'TEACHER',
  })
  createdByRole!: string

  @ApiPropertyOptional({
    type: [CampaignTargetResponse],
    description: 'Liste des cibles de la campagne avec leur statut de livraison',
  })
  targets?: CampaignTargetResponse[]

  @ApiPropertyOptional({
    description:
      'Liste des annonces créées par cette campagne (uniquement si type ANNOUNCEMENT ou MIXED)',
    type: 'array',
  })
  announcements?: unknown[]

  @ApiPropertyOptional({
    description:
      'Liste des emails créés par cette campagne (uniquement si type EMAIL_CAMPAIGN ou MIXED)',
    type: 'array',
  })
  emails?: unknown[]
}

/**
 * Statistiques d'une campagne
 */
export class CampaignStats {
  @ApiProperty({ description: 'Nombre total de cibles' })
  totalTargets!: number

  @ApiProperty({ description: 'Nombre de cibles en attente' })
  pending!: number

  @ApiProperty({ description: "Nombre de cibles en file d'attente" })
  queued!: number

  @ApiProperty({ description: "Nombre de cibles en cours d'envoi" })
  sending!: number

  @ApiProperty({ description: 'Nombre de cibles envoyées avec succès' })
  sent!: number

  @ApiProperty({ description: 'Nombre de cibles échouées' })
  failed!: number

  @ApiProperty({ description: 'Nombre de cibles ignorées' })
  skipped!: number

  @ApiProperty({ description: 'Nombre de cibles ouvertes' })
  opened!: number

  @ApiProperty({ description: 'Taux de livraison (%)' })
  deliveryRate!: number

  @ApiProperty({ description: "Taux d'ouverture (%)" })
  openRate!: number
}

/**
 * Réponse campagne avec statistiques
 */
export class CampaignWithStatsResponse extends CampaignResponse {
  @ApiProperty()
  stats!: CampaignStats
}

/**
 * Réponse paginée de campagnes
 */
export class PaginatedCampaignsResponse {
  @ApiProperty({ type: [CampaignResponse] })
  data!: CampaignResponse[]

  @ApiProperty()
  total!: number

  @ApiProperty()
  page!: number

  @ApiProperty()
  limit!: number

  @ApiProperty()
  totalPages!: number
}
